import type { GbTier } from "@/lib/pricing-utils";

export const PRICING_VERIFIED_DATE = "July 30, 2026";

export const WEBSHARE_URL = "https://www.webshare.io";
export const WEBSHARE_PRICING_URL = "https://www.webshare.io/pricing";
export const WEBSHARE_RESIDENTIAL_URL = "https://www.webshare.io/residential-proxy";
export const WEBSHARE_STATIC_RESIDENTIAL_URL = "https://www.webshare.io/static-residential-proxy";
export const WEBSHARE_DATACENTER_URL = "https://www.webshare.io/proxy-server";
export const WEBSHARE_FAQ_URL = "https://www.webshare.io/frequently-asked-questions";

export type ProxyProduct = "mobile" | "residential";

export interface WebsharePlan {
  name: string;
  gb: number;
  monthlyPrice: number;
  perGb: number;
}

// Webshare's rotating residential proxies — volume-tiered per-GB rate, published
// on webshare.io/pricing. Verified live on PRICING_VERIFIED_DATE.
export const WEBSHARE_RESIDENTIAL_PLANS: WebsharePlan[] = [
  { name: "1 GB", gb: 1, monthlyPrice: 3.5, perGb: 3.5 },
  { name: "10 GB", gb: 10, monthlyPrice: 27.5, perGb: 2.75 },
  { name: "25 GB", gb: 25, monthlyPrice: 65, perGb: 2.6 },
  { name: "50 GB", gb: 50, monthlyPrice: 122.5, perGb: 2.45 },
  { name: "100 GB", gb: 100, monthlyPrice: 225, perGb: 2.25 },
  { name: "250 GB", gb: 250, monthlyPrice: 500, perGb: 2.0 },
  { name: "500 GB", gb: 500, monthlyPrice: 875, perGb: 1.75 },
  { name: "1,000 GB", gb: 1000, monthlyPrice: 1500, perGb: 1.5 },
  { name: "3,000 GB", gb: 3000, monthlyPrice: 4200, perGb: 1.4 },
];

export const WEBSHARE_ANNUAL_NOTE =
  "Webshare also bills annually at a reduced effective per-GB rate on every tier above — the discount size varies by tier, so compare the live annual toggle on their pricing page rather than a single headline number.";

export const WEBSHARE_FREE_DATACENTER_NOTE =
  "Webshare's free plan gives 10 datacenter proxies and 1 GB of bandwidth per month, permanently, with no credit card and no expiry date.";

export const WEBSHARE_STATIC_ISP_NOTE =
  "Webshare's static residential (ISP) proxies are priced per proxy rather than per GB, with unlimited bandwidth subject to a fair usage policy: 20 proxies for $6/month ($0.30/proxy) up to 25% off at 10,000 proxies ($0.225/proxy). Each IP is static for the life of the plan, sourced from real ISPs, with shared, private, or dedicated exclusivity tiers and up to 1 Gbps speeds.";

export const WEBSHARE_DATACENTER_NOTE =
  'Webshare\'s paid datacenter ("Proxy Server") plans start around $2.99/month for 100 proxies ($0.0299/proxy), dropping to about $0.0179/proxy on its largest published tier.';

export const WEBSHARE_ROLLOVER_NOTE =
  "Webshare's help center documents that changing a plan resets its bandwidth usage, and canceling converts any unused value into non-expiring account credit — but it doesn't publish what happens to unused bandwidth on a normal same-plan monthly renewal, so we don't claim it rolls over or resets.";

export const WEBSHARE_REFUND_NOTE =
  "Webshare refunds must be requested within 48 hours of purchase, with usage under 0.1 GB for rotating residential plans or under 1 GB for static residential/proxy server plans, and fewer than 1,000 proxies used. Account credits themselves are non-refundable.";

export const WEBSHARE_KYC_NOTE =
  "Webshare's FAQ says no KYC is required unless suspicious activity is detected; some restricted destination sites may still require a brief KYC step to unblock.";

export const PROXIDIZE_MOBILE_FALLBACK_TIERS: GbTier[] = [
  { gb: 25, monthlyPPG: 2, totalPrice: 50 },
  { gb: 50, monthlyPPG: 2, totalPrice: 100 },
  { gb: 100, monthlyPPG: 2, totalPrice: 200 },
  { gb: 200, monthlyPPG: 2, totalPrice: 400 },
  { gb: 500, monthlyPPG: 2, totalPrice: 1000 },
  { gb: 1000, monthlyPPG: 2, totalPrice: 2000 },
  { gb: 2000, monthlyPPG: 2, totalPrice: 4000 },
  { gb: 5000, monthlyPPG: 2, totalPrice: 10000 },
  { gb: 10000, monthlyPPG: 2, totalPrice: 20000 },
];

export const PROXIDIZE_RESIDENTIAL_FALLBACK_TIERS: GbTier[] = [
  { gb: 25, monthlyPPG: 1, totalPrice: 25 },
  { gb: 50, monthlyPPG: 1, totalPrice: 50 },
  { gb: 100, monthlyPPG: 1, totalPrice: 100 },
  { gb: 200, monthlyPPG: 1, totalPrice: 200 },
  { gb: 500, monthlyPPG: 1, totalPrice: 500 },
  { gb: 1000, monthlyPPG: 1, totalPrice: 1000 },
  { gb: 2000, monthlyPPG: 0.95, totalPrice: 1900 },
  { gb: 5000, monthlyPPG: 0.95, totalPrice: 4750 },
  { gb: 10000, monthlyPPG: 0.9, totalPrice: 9000 },
];

export interface WebshareCost {
  cost: number;
  planLabel: string;
  extrapolated: boolean;
}

// Webshare bills the smallest plan tier that covers the requested volume; above
// its largest published tier (3,000 GB) we extrapolate at that tier's rate.
export function webshareMonthlyCost(gb: number): WebshareCost {
  const plan = WEBSHARE_RESIDENTIAL_PLANS.find((p) => p.gb >= gb);
  if (plan) return { cost: plan.monthlyPrice, planLabel: `${plan.name} plan`, extrapolated: false };
  const top = WEBSHARE_RESIDENTIAL_PLANS[WEBSHARE_RESIDENTIAL_PLANS.length - 1];
  const cost = Math.round(gb * top.perGb * 100) / 100;
  return { cost, planLabel: `${top.name}+ tier rate`, extrapolated: true };
}
